import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { BatchLoader } from "../../../Utils/batch-loader.util";
import PondSectionModel from 'src/app/services/pondSection.model';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import { PondSectionService } from 'src/app/services/pondSection.service';
import CustomerPondModel from 'src/app/services/customerPond.model';
import PondTypeModel from 'src/app/services/pondType.model';
import PondUsageModel from 'src/app/services/pondUse.model';
import { BarCodeService } from 'src/app/services/barCode.service';
import BarcodeModel from 'src/app/services/barCode.model';
import { PondTypesService } from 'src/app/services/pond-types.service';
import { PondUsagesService } from 'src/app/services/pond-usages.service';
import { CustomerPondService } from 'src/app/services/customerPond.service';
import {ImageDetailsService} from 'src/app/services/images-details.service';
import ImageDetailsModel from 'src/app/services/images-details.model';
import * as _ from 'lodash';
import { HttpErrorResponse } from '@angular/common/http';

declare var $: any;

/**
 * SensorComponent
 *
 * This component is responsible for managing Sensors within the Hardware Management module.
 * It provides a user interface for administrators to view, create, update, and search for sensors.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all sensors.
 * - Provide search functionality to filter sensors by name, vendor name, QP name, or sensor type name.
 * - Manage the creation of new sensors with validation.
 * - Handle updates to existing sensors.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update sensors.
 * - **Real-time Search**: Filter list by multiple sensor attributes.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures sensor details follow specific patterns (alphanumeric, spaces).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 * - **Dynamic Dropdowns**: Loads sensor types and query parameters for selection.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all sensors from `SensorService`.
 * 3. Fetches reference data (Sensor Types, Query Params).
 * 4. Formats dates using `moment`.
 * 5. Initializes `BatchLoader` with the full dataset.
 * 6. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 7. `addUpdatePond` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-sensor
 * @example
 * <app-sensor></app-sensor>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-ponds',
  templateUrl: './ponds.component.html',
  styleUrls: ['./ponds.component.scss']
})
export class PondsComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched sensors from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {SensorModel[]}
   * @default []
   */
  public ponds: CustomerPondModel[] = [];

  /**
   * Array holding the currently displayed sensors.
   * This list is populated by the BatchLoader (subset of sensors or filteredSensors).
   * @type {SensorModel[]}
   * @default []
   */
  public pondsList: CustomerPondModel[] = [];

  /**
   * Model for the current sensor being added or edited.
   * Bound to the form for data manipulation.
   * @type {SensorModel}
   */
  public pond: CustomerPondModel = new CustomerPondModel();

  /**
   * Array holding sensors that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {SensorModel[]}
   * @default []
   */
  public filteredPonds: CustomerPondModel[] = [];

  /**
   * Array of available Query Parameters for selection.
   * @type {Array<FarmSiteModel>}
   */
  public sections: Array<PondSectionModel>;

  /**
  * Array of available Query Parameters for selection.
  * @type {Array<FarmSiteModel>}
  */
  public pondtypes: Array<PondTypeModel>;

  /**
  * Array of available Query Parameters for selection.
  * @type {Array<FarmSiteModel>}
  */
  public pondUsages: Array<PondUsageModel>;

  /**
  * Array of available Query Parameters for selection.
  * @type {Array<BarcodeModel>}
  */
  public barcodes: Array<BarcodeModel>;

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public sensorDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Active page number (not actively used in current logic but declared).
   * @type {number}
   */
  public activePageNo: number;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Reactive form group for sensor details.
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  public barcode_sequence: string;

  /** @type {string} Selected pond ID for deletion. */
  public selectedPondId: string;

  /** @type {Array<ImageDetailsModel>} List of image details. */
  public imageDetails: Array<ImageDetailsModel>;

  /** @type {ImageDetailsModel} Single image detail model (unused). */
  public imageDetail: ImageDetailsModel;

  /** @type {string} Selected data type for deletion. */
  public selectedDeletedataType: string;

  showAddEditForm = false;
  showDeleteForm = false;

  /**
   * Constructor for SensorComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - vendor_name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - qp_id: Required.
   * - sensor_type_id: Required.
   *
   * @constructor
   * @param {ToastrService} toastrService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {SensorService} sensorService - Service for Sensor API operations.
   * @service SensorService
   * @param {QueryParamsService} queryParamsService - Service for Query Params API operations.
   * @service QueryParamsService
   * @param {SensorTypeService} sensorTypeService - Service for Sensor Type API operations.
   * @service SensorTypeService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(
    private toastrService: ToastrService,
    public formBuilder: FormBuilder,
    public sidenavaction: Sidenavaction,
    public farmsiteService: FarmSiteService,
    private sectionsService: PondSectionService,
    private barcodeService: BarCodeService,
    private pondtypesService: PondTypesService,
    private pondUsagesService: PondUsagesService,
    private pondsService: CustomerPondService,
    public imageDetailService: ImageDetailsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {

    this.pond = new CustomerPondModel();

    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(30), Validators.pattern('^[a-zA-Z0-9 -]*$')]],
      barcode: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(4), Validators.pattern('^[0-9]*$')]],
      pond_type_id: ['', Validators.required],
      ponduse_id: ['', Validators.required],
      section_id: ['', Validators.required],
      number_of_aerators: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      code: ['', [Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[A-Za-z0-9]*$')]],
      status: ['', [Validators.maxLength(10), Validators.pattern('^[A-Za-z ]*$')]]
    });

    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all sensors from the backend.
   * 5. Fetches reference data (Query Params, Sensor Types).
   * 6. Formats the 'updatedAt' date for each sensor.
   * 7. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.enableDisablePageLoader = true;
      this.clear();
      this.search = '';
      this.ponds = [];
      this.pondsList = [];
      this.filteredPonds = [];
      this.sections = [];
      this.pondtypes = [];
      this.pondUsages = [];
      this.barcodes = [];
      this.NoDataMessage = false;
      this.isFiltering = false;
      this.showDeleteForm = false;
      this.showAddEditForm = false;
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.sidenavaction.x_navigation();
      await this.getBarcodeByBusinessId();
      this.pondtypes = await this.pondtypesService.getAllPondTypesList();
      this.pondUsages = await this.pondUsagesService.getAllPondUsagesList();
      const farmsites = await this.farmsiteService.getFarmeSitesByFarmId(localStorage.loginBusinessId);
      const sections = await this.sectionsService.getAllSections();
      const ponds = await this.pondsService.getAllCustomerPonds();
      this.sections = sections
        .filter(s => farmsites.some(f => f.id === s.farm_site_id))
      this.ponds = ponds
        .filter(s => farmsites.some(f => f.id === s.farm_site_id))
        .map(pond => {
          const farmsite = farmsites.find(f => f.id === pond.farm_site_id);
          const section = sections.find(s => Number(s.id) === Number(pond.section_id));
          const pondType = this.pondtypes.find(p => Number(p.id) === Number(pond.pond_type_id));
          const pondUsage = this.pondUsages.find(p => Number(p.id) === Number(pond.ponduse_id));

          return {
            ...pond,
            farmsiteDetails: farmsite,
            section: section,
            PondType: pondType,
            PondUsage: pondUsage
          };
        });
      console.log(375, this.ponds);

      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
    * @method
    * @async
    * @returns {Promise<void>} A Promise that resolves when barcodes are retrieved and tank's barcode is set.
    * @throws {Error} If there's an error during initialization.
    * Description:
    * This method is used to Retrieves barcodes associated with a specific business ID by calling getBarcodeByBusinessId method
    * which is in the {@link BarCodeService} file.
    */
  public async getBarcodeByBusinessId(): Promise<void> {
    try {
      this.barcodes = await this.barcodeService.getBarcodeByBusinessId(localStorage.loginBusinessID);
      if (this.barcodes.length != 0) {
        this.barcode_sequence = this.barcodes[0].barcode_sequence + '0';
      } else {
        this.barcode_sequence = '';
        this.toastrService.error('No barcode sequence is assigned');
      }
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredPonds : this.ponds,
      (updatedList) => {
        this.pondsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  get f() {
    return this.registerForm.controls;
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for all injected services.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.pondUsagesService.cancelOnGoingHttpRequests();
    this.pondtypesService.cancelOnGoingHttpRequests();
    this.sectionsService.cancelOnGoingHttpRequests();
    this.farmsiteService.cancelOnGoingHttpRequests();
    this.barcodeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Filters the sensor list based on the search query.
   *
   * - Filters by name, vendor name, QP name, or sensor type name (case-insensitive).
   * - Updates `filteredSensors`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredSensors`, `sensorsList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.ponds.filter(pond =>
        pond.name?.toLowerCase().includes(query)
      )
      : this.ponds;
    this.filteredPonds = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.pondsList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.showDeleteForm = false;
    this.showAddEditForm = false;
    this.pond = new CustomerPondModel();
    this.sensorDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  /**
   * Handles the submission of the Sensor form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdatePond
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdatePond() {
    this.submitted = true;
    console.log('pond creation', this.registerForm)
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    navigator.geolocation.getCurrentPosition((position) => {
      this.pond.latitude = position.coords.latitude;
      this.pond.longitude = position.coords.longitude;
    }, (
    ) => {
      this.pond.latitude = 0;
      this.pond.longitude = 0;
    });
    // GET FORM VALUES
    const formData = this.registerForm.value; // contains updated values
    console.log('pond creation form', formData);
    const loginUser = localStorage.getItem('LoginUserName');
    formData.created_name = loginUser;
    formData.updated_name = loginUser;
    try {
      this.sensorDoubleClick = true;
      let barcode;
      if (this.submitButton) {
       try {
          barcode = await this.barcodeService.getBarcodeValidation(
            this.barcode_sequence + formData.barcode
          );
        } catch (error: any) {
          this.toastrService.error(
            error?.error?.message || 'Invalid barcode'
          );

          this.sensorDoubleClick = false;

          return; // IMPORTANT
        }
        
        if (barcode) {
          this.pond.geo_location_id = 1;
          this.pond.isBH = false;
          this.pond.name = formData.name;
          this.pond.pond_code = this.barcode_sequence + formData.barcode;
          this.pond.pond_type_id = formData.pond_type_id;
          this.pond.ponduse_id = formData.ponduse_id;
          this.pond.section_id = formData.section_id;
          this.pond.number_of_aerators = formData.number_of_aerators;
          this.pond.code = formData.code;
          this.pond.status = formData.status;
          this.pond.tank_diameter = 0;
          this.pond.isPreparatory = true;
          this.pond.farm_site_id = this.sections.find(s => s.id === formData.section_id)?.farm_site_id;
          this.pond.section_name = this.sections.find(s => s.id === formData.section_id)?.section_name;
          await this.pondsService.create(this.pond);
          this.toastrService.success('Pond created successfully', '', {
            timeOut: 2000
          });
        }
      } else {
        const updatedPond = { ...formData };
        delete updatedPond.id;
        this.pond.name = updatedPond.name;
        this.pond.pond_code = this.barcode_sequence + formData.barcode;
        this.pond.pond_type_id = updatedPond.pond_type_id;
        this.pond.ponduse_id = updatedPond.ponduse_id;
        this.pond.section_id = updatedPond.section_id;
        this.pond.number_of_aerators = updatedPond.number_of_aerators;
        this.pond.code = updatedPond.code;
        this.pond.status = updatedPond.status;
        this.pond.farm_site_id = this.sections.find(s => s.id === updatedPond.section_id)?.farm_site_id;
        this.pond.section_name = this.sections.find(s => s.id === updatedPond.section_id)?.section_name;
        await this.pondsService.updateOneByID(this.pond.id, updatedPond);
        this.toastrService.success('Pond updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      console.log('error', error);
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.sensorDoubleClick = false;
    }
  }

  /**
   * Retrieves a Sensor by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getSensorById
   * @param {string} id - The ID of the sensor to edit.
   * @returns {void}
   */
  public getPondById(id: string) {
    this.showAddEditForm = true;
    this.showDeleteForm = false;
    this.submitButton = false;
    this.sensorDoubleClick = false;
    this.updateButton = true;
    const foundSensors = this.pondsList.find(uom => uom?.id === id);
    console.log('foundSensors', foundSensors)
    this.pond = foundSensors ? { ...foundSensors } : new CustomerPondModel(); // clone object
    if (this.pond) {
      this.registerForm.patchValue({
        name: this.pond.name,
        barcode: this.pond.pond_code.slice(this.barcode_sequence.length),
        pond_type_id: this.pond.pond_type_id,
        ponduse_id: this.pond.ponduse_id,
        section_id: this.pond.section_id,
        number_of_aerators: this.pond.number_of_aerators,
        code: this.pond.code,
        status: this.pond.status,
        farm_site_id: this.pond.farm_site_id,
      });
      console.log('edit form', this.registerForm.value, this.pond, this.pond.pond_code.slice(this.barcode_sequence.length))
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showAddEditForm = true;
    this.showDeleteForm = false;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

   /**
   * @method getPondRelatedData
   * @async
   * @description Fetches related data (images) for a specific pond to prepare for deletion.
   * @param {string} pondId - The pond ID.
   */
  public async getPondRelatedData(pondId: string) {
    this.showDeleteForm = true;
    this.showAddEditForm = false;
    try {
      this.selectedPondId = _.clone(pondId);
      this.imageDetails = await this.imageDetailService.getPonds([pondId]);
      const keys = Object.keys(this.imageDetails[0]);
      const values = Object.values(this.imageDetails[0]);
      let text = '';
      for (let i = 0; i < keys.length; i++) {
        text += keys[i] + '-' + values[i] + '\n';
      }
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method deletePond
   * @async
   * @description Deletes pond data based on selected type (Data only or Data + Pond).
   */
  public async deletePond() {
    try {
      if (!this.selectedDeletedataType) {
        this.toastrService.error('Please select "Delete Culture Data" or "Delete Culture Data and Pond"');
        return false;
      }

      // No need to flip the value manually
      let pond_delete = this.selectedDeletedataType;  

      this.imageDetails = await this.imageDetailService.deletePond([this.selectedPondId], pond_delete);

      this.toastrService.success(
        `${this.selectedDeletedataType === 'True' ? 'Data and Pond' : 'Data'} deleted successfully`, 
        '', 
        { timeOut: 2000 }
      );
      this.selectedDeletedataType = '';
      this.ngOnInit();
    } catch (error) {
      alert(error);
    }
  }

  /**
   * @method clear
   * @description Clears the deletion selection and hides the form.
   */
  public clearDeletePondPopup() {
    this.selectedDeletedataType = undefined;
    this.showDeleteForm = false;
    this.showAddEditForm = false;
  }
}
